/**
 * Input primitive.
 *
 * Token-backed accessible text input. No Radix dependency (a native
 * `<input>` is already the correct keyboard/screen-reader semantics; the
 * label, hint, and error wiring that consumers need sit on the Form
 * component layer, not here). Every class below is a token-backed
 * Tailwind utility — no arbitrary-bracket classes (FR-047), no raw
 * colour literals (FR-002).
 *
 * The sanctioned focus treatment matches Button's:
 * `focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring
 * focus-visible:outline-none`. `ring-ring` resolves through `--color-ring`
 * in `tokens.css`, which tracks the brand-green primary token so the
 * focus indicator carries the brand colour at AA contrast on the input
 * surface (FR-033). Light scheme only in Cluster 1; dark mode is
 * deferred (see `tokens.css` for rationale).
 *
 * `disabled` and `readonly` states are handled visually through
 * token-backed utilities: `disabled:opacity-50 disabled:cursor-not-allowed`
 * mirrors Button's disabled treatment; `read-only:bg-muted`
 * disambiguates a read-only field from an enabled editable one using the
 * muted surface token. `min-h-11` (44px) satisfies the FR-036 mobile
 * tap-target minimum; `w-full` makes the input fill its form slot by
 * default — callers wanting a narrower field pass `className="w-32"`
 * or similar, and because the caller's `className` lands last in `cn(...)`
 * the override wins.
 *
 * Radius stays at `rounded-md` (0.625rem — tighter than the shell's
 * `rounded-lg` button treatment) so inputs read with a calmer corner
 * than action buttons, matching the prototype's input/control posture.
 *
 * `type` defaults to `"text"` but passes through so callers can request
 * `email`, `password`, `search`, `tel`, `url`, `number`, etc. without
 * losing the token styling. Ref is a plain prop per React 19 conventions;
 * no `forwardRef` wrapper is needed.
 */

import type { InputHTMLAttributes, Ref } from 'react';

import { cn } from './utils.js';

/**
 * `Input` size scale. `default` is the historical field; `hero` is the
 * proto-aligned large field introduced for the 027 wizard date step (owner
 * finding #5): generous padding, `rounded-xl`, `border-2`, primary focus
 * border, responsive type scale, and built-in trailing-icon clearance
 * (`pr-12`/`sm:pr-14` for the in-field calendar trigger). A typed prop, not
 * a caller `className` override: `cn()` is plain concatenation, so competing
 * same-property classes resolve by Tailwind compile order, not intent —
 * exactly one padding/radius/border-width scale must be emitted.
 */
export type InputSize = 'default' | 'hero';

// The native `size` attribute (character width) is shadowed by the typed
// scale — no consumer uses the character-width form.
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  ref?: Ref<HTMLInputElement>;
  readonly size?: InputSize;
}

const baseClasses =
  'block w-full min-h-11 ' +
  'bg-background text-foreground placeholder:text-muted-foreground ' +
  'transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed ' +
  'read-only:bg-muted read-only:cursor-default';

// Longhand-after-shorthand (`p-4 pr-12`) is Tailwind's sanctioned intra-string
// composition (the generated sheet orders longhands after shorthands); the
// forbidden pattern is competing classes across cn() argument boundaries.
const sizeClasses = {
  default: 'px-3 py-2 text-base border border-border rounded-md',
  hero:
    'p-4 pr-12 sm:p-6 sm:pr-14 text-base sm:text-lg border-2 border-border rounded-xl ' +
    'focus:border-primary',
} as const satisfies Record<InputSize, string>;

export function Input({ type = 'text', className, size = 'default', ref, ...rest }: InputProps) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(baseClasses, sizeClasses[size], className)}
      {...rest}
    />
  );
}
